import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Schedule,
  CheckCircle,
  Cancel,
  AttachMoney,
  Timer,
  LocationOn,
  PlayArrow,
  SignalWifiOff,
  CloudDownload
} from '@mui/icons-material';
import simpleVerificationService from '../services/simpleVerificationService';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { prepareForOffline } from '../services/preflightSyncService';
import { useAuth } from '../contexts/AuthContext';
import OfflineGuideDialog from '../components/verifier/OfflineGuideDialog';

/**
 * My Simple Verifications Page
 * Shows verifier's verification history and earnings
 */
const MySimpleVerificationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(location.state?.message || null);
  const [startDialog, setStartDialog] = useState({ open: false, verification: null });
  const [startingVerification, setStartingVerification] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [preparingOffline, setPreparingOffline] = useState(false);
  const [offlinePrepared, setOfflinePrepared] = useState(false);
  const [showOfflineGuide, setShowOfflineGuide] = useState(false);

  const statusFilters = ['all', 'assigned', 'in_progress', 'completed', 'approved'];

  useEffect(() => {
    loadVerifications();
  }, [activeTab]);

  // Clear the info message after showing it
  useEffect(() => {
    if (infoMessage) {
      const timer = setTimeout(() => setInfoMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [infoMessage]);

  const loadVerifications = async () => {
    try {
      setLoading(true);
      const status = activeTab === 0 ? undefined : statusFilters[activeTab];
      const response = await simpleVerificationService.getMyVerifications(status);
      
      if (response.success) {
        setVerifications(response.data);
      }
    } catch (err) {
      console.error('Error loading verifications:', err);
      setError('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'info',
      in_progress: 'warning',
      completed: 'success',
      approved: 'success',
      disputed: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      assigned: <Schedule />,
      in_progress: <Timer />,
      completed: <CheckCircle />,
      approved: <CheckCircle />,
      disputed: <Cancel />
    };
    return icons[status] || <Schedule />;
  };

  const handleViewReport = (verificationId) => {
    navigate(`/verifier/simple-verifications/${verificationId}/report`);
  };

  const handleContinue = (verificationId) => {
    navigate(`/verifier/verify/${verificationId}`);
  };

  const handleStartClick = (verification) => {
    setStartDialog({ open: true, verification });
    setGpsError(null);
  };

  const handleStartConfirm = async () => {
    const { verification } = startDialog;
    setStartingVerification(true);
    setGpsError(null);

    try {
      // Try to get GPS location, but don't block if it fails
      let gpsLocation = null;
      
      if (navigator.geolocation) {
        // Attempt to get GPS with shorter timeout
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              gpsLocation = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy
              };
              resolve();
            },
            (err) => {
              console.warn('GPS unavailable, continuing without location:', err);
              // Set a warning but don't block
              setGpsError('Location unavailable - continuing without GPS');
              resolve();
            },
            {
              enableHighAccuracy: false,
              timeout: 3000,
              maximumAge: 60000
            }
          );
        });
      }

      // Start verification with or without GPS
      await simpleVerificationService.startVerification(verification.id, gpsLocation);

      // Close dialog and navigate
      setStartDialog({ open: false, verification: null });
      setStartingVerification(false);
      navigate(`/verifier/verify/${verification.id}`);
    } catch (err) {
      console.error('Error starting verification:', err);
      setGpsError(err.response?.data?.message || 'Failed to start verification');
      setStartingVerification(false);
    }
  };

  const handleStartCancel = () => {
    setStartDialog({ open: false, verification: null });
    setGpsError(null);
  };

  // Calculate stats
  const stats = {
    total: verifications.length,
    completed: verifications.filter(v => v.status === 'approved').length,
    earnings: verifications
      .filter(v => v.status === 'approved')
      .reduce((sum, v) => sum + (v.cost || 0), 0),
    avgRating: verifications
      .filter(v => v.aiQualityScore)
      .reduce((sum, v, _, arr) => sum + v.aiQualityScore / arr.length, 0) || 0
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" gutterBottom>
          My Verifications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your verification history and earnings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary" fontWeight="700">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Verifications
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" fontWeight="700">
              {stats.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" fontWeight="700">
              ${stats.earnings.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Earnings
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main" fontWeight="700">
              {stats.avgRating.toFixed(0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg Quality Score
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" />
          <Tab label="Assigned" />
          <Tab label="In Progress" />
          <Tab label="Completed" />
          <Tab label="Approved" />
        </Tabs>
      </Paper>

      {/* Verifications List */}
      {verifications.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {activeTab === 0 
                ? 'No verifications available yet' 
                : `No ${statusFilters[activeTab].replace('_', ' ')} verifications`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {activeTab === 0 
                ? 'Verifications will appear here once they are assigned to you.'
                : 'Try selecting a different tab to view other verifications.'}
            </Typography>
            {activeTab !== 0 && (
              <Button 
                variant="outlined" 
                onClick={() => setActiveTab(0)}
              >
                View All Verifications
              </Button>
            )}
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {verifications.map((verification) => (
            <Grid item xs={12} md={6} key={verification.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" fontWeight="600">
                      {verification.title}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(verification.status)}
                      label={verification.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(verification.status)}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<AttachMoney />}
                      label={`$${verification.cost}`}
                      size="small"
                      color="success"
                    />
                    {verification.verificationDuration && (
                      <Chip
                        icon={<Timer />}
                        label={`${verification.verificationDuration} mins`}
                        size="small"
                      />
                    )}
                    {verification.aiQualityScore && (
                      <Chip
                        label={`Quality: ${verification.aiQualityScore}/100`}
                        size="small"
                        color="warning"
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block">
                    Project: {verification.projectName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(verification.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>

                <CardActions>
                  {verification.status === 'assigned' && (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={() => handleStartClick(verification)}
                    >
                      Start Verification
                    </Button>
                  )}
                  {verification.status === 'in_progress' && (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleContinue(verification.id)}
                    >
                      Continue
                    </Button>
                  )}
                  {(verification.status === 'completed' || verification.status === 'approved') && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleViewReport(verification.id)}
                    >
                      View Report
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Start Verification Confirmation Dialog */}
      <Dialog open={startDialog.open} onClose={handleStartCancel} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={handleStartCancel} />
        <DialogTitle>
          Start Verification
        </DialogTitle>
        <DialogContent>
          {startDialog.verification && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                You are about to start the following verification:
              </DialogContentText>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {startDialog.verification.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Type: {startDialog.verification.verificationType?.replace('_', ' ').toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cost: ${startDialog.verification.cost}
                </Typography>
              </Paper>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>GPS Location (Optional)</strong><br />
                Your location will be captured if available to verify you are at the site.
              </Alert>

              {/* Offline preparation option */}
              <Paper
                sx={{
                  p: 2, mb: 2,
                  border: '1px dashed',
                  borderColor: offlinePrepared ? 'success.main' : 'info.main',
                  bgcolor: offlinePrepared ? 'success.50' : 'info.50',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SignalWifiOff sx={{ color: offlinePrepared ? 'success.main' : 'info.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {offlinePrepared ? 'Ready for Offline' : 'Going to a site with poor internet?'}
                  </Typography>
                  {!offlinePrepared && (
                    <Chip
                      label="Learn more"
                      size="small"
                      variant="outlined"
                      onClick={() => setShowOfflineGuide(true)}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
                {offlinePrepared ? (
                  <Typography variant="body2" color="success.dark">
                    All data cached. You can complete this verification fully offline at the site.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Prepare now to complete the entire verification offline — photos, GPS, checklist, report, and signature.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={preparingOffline ? <CircularProgress size={14} /> : <CloudDownload />}
                      disabled={preparingOffline}
                      onClick={async () => {
                        setPreparingOffline(true);
                        try {
                          await prepareForOffline(startDialog.verification.id, {
                            userId: user?.id,
                            name: user?.profile?.first_name || user?.name,
                            tier: user?.profile?.tier || 'general'
                          });
                          setOfflinePrepared(true);
                        } catch (err) {
                          console.error('Offline prep failed:', err);
                          setGpsError('Failed to prepare offline: ' + (err.message || 'Unknown error'));
                        } finally {
                          setPreparingOffline(false);
                        }
                      }}
                    >
                      {preparingOffline ? 'Preparing...' : 'Prepare for Offline'}
                    </Button>
                  </>
                )}
              </Paper>

              {gpsError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {gpsError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStartCancel} disabled={startingVerification}>
            Cancel
          </Button>
          <Button
            onClick={handleStartConfirm}
            variant="contained"
            color="primary"
            disabled={startingVerification}
            startIcon={startingVerification ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {startingVerification ? 'Starting...' : 'Start Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offline Guide Dialog */}
      <OfflineGuideDialog
        open={showOfflineGuide}
        onClose={() => setShowOfflineGuide(false)}
        onPrepareOffline={startDialog.verification ? async () => {
          setPreparingOffline(true);
          try {
            await prepareForOffline(startDialog.verification.id, {
              userId: user?.id,
              name: user?.profile?.first_name || user?.name,
              tier: user?.profile?.tier || 'general'
            });
            setOfflinePrepared(true);
          } catch (err) {
            setGpsError('Failed to prepare offline: ' + (err.message || 'Unknown error'));
          } finally {
            setPreparingOffline(false);
          }
        } : null}
      />
    </Container>
  );
};

export default MySimpleVerificationsPage;
